import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import MasterEditModal from '../../components/MasterEditModal';
import { getResultLookupType, normalizeResultOptions } from '../../utils/resultOptions';

export default function InspectionTypes() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    key: '',
    name: '',
    name_en: '',
    icon: '',
    route_path: '',
    done_field: '',
    save_endpoint: '',
    supports_items: true,
    is_active: true,
    sort_order: 1,
  });
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bodyLookups, setBodyLookups] = useState([]);
  const [genericLookups, setGenericLookups] = useState([]);

  const load = () => api.get('/inspection-types').then((r) => setRows(r.data || []));

  useEffect(() => {
    load();
  }, []);

  // النتائج الثابتة المتاحة للربط: البودي له قائمته والباقي قائمته.
  useEffect(() => {
    api.get(`/lookups/by-type/${getResultLookupType('body')}`)
      .then((r) => setBodyLookups(r.data || []))
      .catch(() => setBodyLookups([]));

    api.get(`/lookups/by-type/${getResultLookupType('generic')}`)
      .then((r) => setGenericLookups(r.data || []))
      .catch(() => setGenericLookups([]));
  }, []);

  const optionsForType = useCallback((typeKey) => {
    const kind = String(typeKey || '').toLowerCase() === 'body' ? 'body' : 'generic';
    const rowsForKind = kind === 'body' ? bodyLookups : genericLookups;
    return normalizeResultOptions(rowsForKind, kind);
  }, [bodyLookups, genericLookups]);

  const editOptions = useMemo(
    () => optionsForType(editForm?.key).map((option) => ({
      value: option.value,
      label: option.label,
      color: option.bgColor,
    })),
    [optionsForType, editForm?.key],
  );

  const resultLabels = useCallback((row) => {
    const linked = Array.isArray(row?.allowed_result_types) ? row.allowed_result_types : [];
    if (!linked.length) return 'الكل';

    const byValue = new Map(optionsForType(row?.key).map((option) => [option.value, option.label]));
    return linked.map((value) => byValue.get(value) || value).join('، ');
  }, [optionsForType]);

  const setField = (key, value) => setForm((s) => ({ ...s, [key]: value }));

  const add = async () => {
    if (!form.key || !form.name) return;
    await api.post('/inspection-types', {
      ...form,
      sort_order: Number(form.sort_order) || 0,
    });
    setForm({
      key: '',
      name: '',
      name_en: '',
      icon: '',
      route_path: '',
      done_field: '',
      save_endpoint: '',
      supports_items: true,
      is_active: true,
      sort_order: 1,
    });
    load();
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      key: row.key || '',
      name: row.name || '',
      name_en: row.name_en || '',
      icon: row.icon || '',
      route_path: row.route_path || '',
      done_field: row.done_field || '',
      save_endpoint: row.save_endpoint || '',
      supports_items: Boolean(row.supports_items),
      is_active: Boolean(row.is_active),
      sort_order: row.sort_order ?? 0,
      allowed_result_types: Array.isArray(row.allowed_result_types) ? row.allowed_result_types : [],
    });
  };

  const update = async () => {
    if (!editForm?.key.trim() || !editForm?.name.trim() || !editRow) return;
    setSaving(true);
    try {
      await api.put(`/inspection-types/${editRow.id}`, {
        ...editForm,
        allowed_result_types: editForm.allowed_result_types || [],
        name_en: editForm.name_en || null,
        icon: editForm.icon || null,
        route_path: editForm.route_path || null,
        done_field: editForm.done_field || null,
        save_endpoint: editForm.save_endpoint || null,
        sort_order: Number(editForm.sort_order) || 0,
      });
      setEditRow(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    await api.put(`/inspection-types/${row.id}`, {
      key: row.key,
      name: row.name,
      name_en: row.name_en,
      icon: row.icon,
      route_path: row.route_path,
      done_field: row.done_field,
      save_endpoint: row.save_endpoint,
      supports_items: row.supports_items,
      allowed_result_types: row.allowed_result_types || [],
      is_active: !row.is_active,
      sort_order: row.sort_order,
    });
    load();
  };

  const remove = async (row) => {
    if (!window.confirm('تأكيد حذف نوع الفحص؟')) return;
    await api.delete(`/inspection-types/${row.id}`);
    load();
  };

  return (
    <div>
      <div className="grid md:grid-cols-5 gap-2 mb-3">
        <input className="rounded-lg border p-2" placeholder="key مثال: brakes" value={form.key} onChange={(e) => setField('key', e.target.value)} />
        <input className="rounded-lg border p-2" placeholder="اسم النوع" value={form.name} onChange={(e) => setField('name', e.target.value)} />
        <input className="rounded-lg border p-2" placeholder="English name" value={form.name_en} onChange={(e) => setField('name_en', e.target.value)} />
        <input className="rounded-lg border p-2" placeholder="icon" value={form.icon} onChange={(e) => setField('icon', e.target.value)} />
        <input className="rounded-lg border p-2" placeholder="sort_order" type="number" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} />
      </div>

      <div className="grid md:grid-cols-3 gap-2 mb-3">
        <input className="rounded-lg border p-2" placeholder="route_path مثال: brakes" value={form.route_path} onChange={(e) => setField('route_path', e.target.value)} />
        <input className="rounded-lg border p-2" placeholder="done_field مثال: brakes_done" value={form.done_field} onChange={(e) => setField('done_field', e.target.value)} />
        <input className="rounded-lg border p-2" placeholder="save_endpoint مثال: brakes" value={form.save_endpoint} onChange={(e) => setField('save_endpoint', e.target.value)} />
      </div>

      <div className="flex flex-wrap gap-4 mb-4">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={form.supports_items} onChange={(e) => setField('supports_items', e.target.checked)} />
          يدعم بنود فحص
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setField('is_active', e.target.checked)} />
          نشط
        </label>

        <button onClick={add} className="rounded-lg bg-primary px-4 py-2 text-white">إضافة نوع الفحص</button>
      </div>

      <table className="w-full text-sm border rounded-lg overflow-hidden">
        <thead className="bg-slate-100">
          <tr>
            <th className="p-2 text-right">النوع</th>
            <th className="p-2 text-right">English name</th>
            <th className="p-2 text-right">key</th>
            <th className="p-2 text-right">route</th>
            <th className="p-2 text-right">done_field</th>
            <th className="p-2 text-right">النتائج المرتبطة</th>
            <th className="p-2 text-right">status</th>
            <th className="p-2 text-right">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="p-2">{r.icon ? `${r.icon} ` : ''}{r.name}</td>
              <td className="p-2">{r.name_en || '-'}</td>
              <td className="p-2 font-mono text-xs">{r.key}</td>
              <td className="p-2 font-mono text-xs">{r.route_path || '-'}</td>
              <td className="p-2 font-mono text-xs">{r.done_field || '-'}</td>
              <td className="p-2 text-xs">{resultLabels(r)}</td>
              <td className="p-2">{r.is_active ? 'نشط' : 'موقف'}</td>
              <td className="p-2 flex flex-wrap gap-2">
                <button onClick={() => openEdit(r)} className="px-2 py-1 border rounded">تعديل</button>
                <button onClick={() => toggleActive(r)} className="px-2 py-1 border rounded">{r.is_active ? 'إيقاف' : 'تفعيل'}</button>
                <button onClick={() => remove(r)} className="px-2 py-1 border rounded text-red-600">حذف</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <MasterEditModal
        open={Boolean(editRow && editForm)}
        title="تعديل نوع الفحص"
        subtitle="كل حقول نوع الفحص متاحة هنا للحفظ دفعة واحدة."
        fields={[
          { name: 'key', label: 'المفتاح', required: true, placeholder: 'brakes' },
          { name: 'name', label: 'اسم النوع', required: true },
          { name: 'name_en', label: 'English name' },
          { name: 'icon', label: 'الأيقونة' },
          { name: 'route_path', label: 'route_path', placeholder: 'brakes' },
          { name: 'done_field', label: 'done_field', placeholder: 'brakes_done' },
          { name: 'save_endpoint', label: 'save_endpoint', placeholder: 'brakes' },
          { name: 'sort_order', label: 'الترتيب', type: 'number' },
          {
            name: 'allowed_result_types',
            label: 'النتائج الثابتة المرتبطة بهذا النوع',
            type: 'multicheck',
            fullWidth: true,
            hint: 'اترك الكل بلا تحديد لإتاحة جميع النتائج. ما لا تختاره هنا لن يظهر في شاشة بنود الفحص.',
            options: editOptions,
          },
          { name: 'supports_items', label: 'يدعم بنود فحص', type: 'checkbox' },
          { name: 'is_active', label: 'نشط', type: 'checkbox' },
        ]}
        values={editForm || {}}
        onChange={(key, value) => setEditForm((current) => ({ ...current, [key]: value }))}
        onClose={() => setEditRow(null)}
        onSubmit={update}
        saving={saving}
      />
    </div>
  );
}
